import type { LessonIndexItem } from './tutor.client';

/** v2 keys include tenant/school scope to prevent cross-tenant collisions in one browser. */
const CACHE_PREFIX = 'tkm_tutor_cache_v2_';
const LEGACY_CACHE_PREFIX = 'tkm_tutor_cache_';
const LESSON_INDEX_TTL_MS = 10 * 60 * 1000;
const SYSTEM_INSTRUCTION_TTL_MS = 15 * 60 * 1000;

type CachedPayload<T> = {
  data: T;
  expiresAt: number;
};

function normalizeScope(scope: string | null | undefined): string {
  const s = String(scope || '').trim();
  return s || 'unknown';
}

function lessonIndexStorageKey(grade: number, tenantScope?: string | null): string {
  return `${CACHE_PREFIX}lesson_index_${normalizeScope(tenantScope)}_${grade}`;
}

function systemInstructionStorageKey(key: string): string {
  return `${CACHE_PREFIX}si_${key}`;
}

function readCachedPayload<T>(storageKey: string): T | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload<T>;
    if (!parsed || parsed.expiresAt == null || parsed.data == null) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCachedPayload<T>(storageKey: string, data: T, ttlMs: number): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const payload: CachedPayload<T> = {
      data,
      expiresAt: Date.now() + ttlMs,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    /* quota or private mode — ignore */
  }
}

function removeCachedPayload(storageKey: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    /* ignore */
  }
}

/** Clear legacy v1 keys that omitted tenant scope (one-time safe migration). */
export function clearLegacyTutorBootstrapCacheKeys(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(LEGACY_CACHE_PREFIX) && !k.startsWith(CACHE_PREFIX)) {
        keys.push(k);
      }
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export function buildSystemInstructionCacheKey(params: {
  grade: number;
  lessonNo: number;
  sublesson?: number | string | null;
  dialectKey: string;
  topicKey?: string;
  tenantScope?: string | null;
  activityKey?: string | null;
  contentVersion?: string | null;
}): string | null {
  const dialect = String(params.dialectKey || '').trim();
  if (!dialect) return null;
  const sub = params.sublesson ?? 0;
  const scope = normalizeScope(params.tenantScope);
  const activity = String(params.activityKey || '').trim() || 'none';
  const version = String(params.contentVersion || '').trim() || 'na';
  const base = `${scope}:${params.grade}:${params.lessonNo}:${sub}:${dialect}:${activity}:${version}`;
  const topic = String(params.topicKey || '').trim();
  return topic ? `${base}:${topic}` : base;
}

export function getCachedLessonIndex(
  grade: number,
  tenantScope?: string | null
): LessonIndexItem[] | null {
  clearLegacyTutorBootstrapCacheKeys();
  const data = readCachedPayload<LessonIndexItem[]>(lessonIndexStorageKey(grade, tenantScope));
  if (!Array.isArray(data) || data.length === 0) return null;
  return data;
}

export function setCachedLessonIndex(
  grade: number,
  data: LessonIndexItem[],
  tenantScope?: string | null
): void {
  writeCachedPayload(lessonIndexStorageKey(grade, tenantScope), data, LESSON_INDEX_TTL_MS);
}

export function invalidateLessonIndexCache(grade: number, tenantScope?: string | null): void {
  removeCachedPayload(lessonIndexStorageKey(grade, tenantScope));
}

export function getCachedSystemInstruction(key: string): string | null {
  const trimmed = String(key || '').trim();
  if (!trimmed) return null;
  const value = readCachedPayload<string>(systemInstructionStorageKey(trimmed));
  return typeof value === 'string' && value.trim() ? value : null;
}

export function setCachedSystemInstruction(key: string, value: string): void {
  const trimmed = String(key || '').trim();
  const instruction = String(value || '').trim();
  if (!trimmed || !instruction) return;
  writeCachedPayload(systemInstructionStorageKey(trimmed), instruction, SYSTEM_INSTRUCTION_TTL_MS);
}

export function invalidateSystemInstructionCache(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(`${CACHE_PREFIX}si_`) || k?.startsWith(`${LEGACY_CACHE_PREFIX}si_`)) {
        keys.push(k);
      }
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export function clearTutorBootstrapCache(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(CACHE_PREFIX) || k?.startsWith(LEGACY_CACHE_PREFIX)) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}
